/* assets/src/viewer/core/lighting.js
 * Luci + coreografia sole fisico (port applyViewerShadowState :189-239) adattata a
 * pipeline ACES + IBL da Sky. Il sole guida sia la DirectionalLight (ombre) sia il cielo.
 */
import * as THREE from 'three';
import { clamp, getViewerSettings } from '../settings.js';

function orientationAngle(orientation) {
  switch (String(orientation || 'south').toLowerCase()) {
    case 'north': return Math.PI;
    case 'east': return -Math.PI / 2;
    case 'west': return Math.PI / 2;
    case 'south':
    default: return 0;
  }
}

export class Lighting {
  constructor(scene, renderer) {
    this.scene = scene;
    this.renderer = renderer;
    this.skyEnv = null;
    // Default: pomeriggio (sole angolato) -> ombre più lunghe e leggibili, layout meno piatto.
    this.state = { enabled: true, orientation: 'south', hour: 15 };
    this.mode = 'day';

    const settings = getViewerSettings();

    this.hemi = new THREE.HemisphereLight(0xbdd2ff, 0x4a4640, 0.4);
    scene.add(this.hemi);

    this.key = new THREE.DirectionalLight(0xfff4e6, 2.4);
    this.key.position.set(8, 14, 6);
    this.key.castShadow = settings.shadows;
    this.key.shadow.mapSize.set(2048, 2048);
    this.key.shadow.bias = -0.00018;
    this.key.shadow.normalBias = 0.025;
    this.key.shadow.radius = 1.7;
    this.key.shadow.camera.left = -16;
    this.key.shadow.camera.right = 16;
    this.key.shadow.camera.top = 16;
    this.key.shadow.camera.bottom = -16;
    this.key.shadow.camera.near = 1;
    this.key.shadow.camera.far = 90;
    scene.add(this.key);
    scene.add(this.key.target);

    this.fill = new THREE.DirectionalLight(0xcfe0ff, 0.3);
    this.fill.position.set(-8, 6, -8);
    scene.add(this.fill);

    this.rim = new THREE.DirectionalLight(0xffffff, 0.18);
    this.rim.position.set(0, 8, -12);
    scene.add(this.rim);
  }

  setSky(skyEnv) {
    this.skyEnv = skyEnv;
  }

  applyState({ regenEnv = false } = {}) {
    const settings = getViewerSettings();
    const hour = clamp(parseInt(this.state.hour != null ? this.state.hour : 12, 10) || 12, 6, 20);
    const enabled = this.state.enabled !== false;
    const isNight = this.mode === 'night';

    const dayProgress = (hour - 6) / 14;            // 0..1
    const elevation = Math.sin(dayProgress * Math.PI); // 0 ad alba/tramonto, 1 a mezzogiorno
    const azimuth = orientationAngle(this.state.orientation) + (dayProgress - 0.5) * Math.PI * 0.95;
    const altAngle = clamp(0.06 + elevation * 0.92, 0.05, 0.98) * (Math.PI / 2);

    const radius = 42;
    const horiz = Math.cos(altAngle) * radius;
    const px = Math.cos(azimuth) * horiz;
    const pz = Math.sin(azimuth) * horiz;
    const py = Math.sin(altAngle) * radius + 2;

    this.key.position.set(px, py, pz);
    this.key.target.position.set(0, 1.4, 0);
    this.key.target.updateMatrixWorld();
    this.key.intensity = isNight ? 0.12 : (enabled ? (0.7 + elevation * 2.1) : 0.7);
    this.key.castShadow = enabled;
    this.key.color.setHSL(isNight ? 0.62 : (0.09 - elevation * 0.05), isNight ? 0.5 : 0.45, isNight ? 0.6 : 0.6);

    this.fill.position.set(-px * 0.6, Math.max(4, py * 0.5), -pz * 0.6);
    this.fill.intensity = isNight ? 0.06 : (0.09 + elevation * 0.08);

    this.rim.position.set(-px * 0.25, Math.max(5, py * 0.7), pz * 0.85);
    this.rim.intensity = isNight ? 0.12 : 0.14;

    // Ambiente più basso del cielo -> le ombre del sole risaltano (non si "lavano").
    this.hemi.intensity = isNight ? 0.12 : (0.13 + elevation * 0.13);
    this.hemi.color.setHSL(isNight ? 0.62 : 0.6, 0.4, isNight ? 0.25 : 0.75);
    this.hemi.groundColor.setHSL(0.09, 0.3, isNight ? 0.04 : 0.22);

    if (this.skyEnv) {
      this.skyEnv.setNight(isNight);
      this.skyEnv.setSunDirection(new THREE.Vector3(px, py, pz));
      if (regenEnv) this.skyEnv.updateEnvironment();
    }

    if (this.renderer) {
      const exposure = settings.exposure || 1.0;
      // ACES + cielo molto luminoso → esposizione contenuta; di notte più bassa.
      this.renderer.toneMappingExposure = isNight ? clamp(exposure * 0.58, 0.32, 0.95) : clamp(exposure * 0.78, 0.5, 1.15);
      this.renderer.shadowMap.enabled = enabled;
    }
  }

  updateSun(settings = {}) {
    this.state = { ...this.state, ...settings };
    // Luce + direzione sole + cielo SUBITO (fluido durante il drag dello slider ora);
    // la rigenerazione PMREM (costosa) è DEBOUNCED → niente hitch/lag.
    this.applyState({ regenEnv: false });
    this._scheduleEnvRegen();
  }

  _scheduleEnvRegen() {
    if (this._envTimer) clearTimeout(this._envTimer);
    this._envTimer = setTimeout(() => {
      this._envTimer = 0;
      if (this.skyEnv) this.skyEnv.updateEnvironment();
    }, 200);
  }

  setMode(mode = 'day') {
    this.mode = String(mode || 'day').toLowerCase() === 'night' ? 'night' : 'day';
    this.applyState({ regenEnv: true });
  }
}
